// CR-03 amends the WARNING flash to show the specific reason instead of the
// generic "WARNING" text. The visual style (yellow background, warning icon)
// and the OK / REJECTED flashes are unchanged.
//
// CR-03 follow-up: two more red states for "QR not recognised / something
// wrong" cases that previously fell through to "REJECTED" (which means
// "not registered" — misleading for those failures).
//
// CR-04 adds two more yellow states for stage-specific warnings:
// STAGE_FULL (recorded warning) and STAGE_NOT_ACTIVE (rejected; can be
// either "Stage closed" or "Stage not active yet" depending on which side of
// the window now() falls on — the caller picks).
//
// CR-07 adds an identifying snapshot under the label — attendee name on
// row 3, company + group names joined by ` · ` on row 4 (wrapping to at
// most three lines). Position, colours, icon, label wording, label
// typography, and the 1.5s fade are unchanged. The snapshot is hidden
// for REJECTED / INVALID_QR / CONNECTION_ERROR (no attendee resolved).
//
// Post-CR-07 follow-up: the red flashes carry an optional `reason` line
// under the label so the operator sees *what* was rejected — the
// scanned badge UUID for NOT_REGISTERED (and the 400 path) and the raw
// decoded QR text for the pre-send INVALID_QR pre-check. CONNECTION_ERROR
// is intentionally without a reason line (the label already says it all).
//
// - OK                            — green ✓ "OK"
// - WARNING_ALREADY_IN            — yellow ⚠ "Already in room"
// - WARNING_ALREADY_OUT           — yellow ⚠ "Already exited"
// - WARNING_STAGE_FULL            — yellow ⚠ "Stage at capacity"
// - WARNING_STAGE_NOT_ACTIVE_BEFORE — yellow ⚠ "Stage not active yet"
// - WARNING_STAGE_NOT_ACTIVE_AFTER  — yellow ⚠ "Stage closed"
// - REJECTED                      — red ✕ "REJECTED"            (not registered for event)
// - STAGE_NOT_ELIGIBLE            — red ✕ "Not allowed in this stage" (CR-08; not registered for the stage's MP program)
// - INVALID_QR                    — red ✕ "Invalid QR code"     (malformed badge / 400)
// - CONNECTION_ERROR              — red ✕ "Connection error"    (network / 5xx / other)
export type FlashResult =
  | "OK"
  | "WARNING_ALREADY_IN"
  | "WARNING_ALREADY_OUT"
  | "WARNING_STAGE_FULL"
  | "WARNING_STAGE_NOT_ACTIVE_BEFORE"
  | "WARNING_STAGE_NOT_ACTIVE_AFTER"
  | "REJECTED"
  | "STAGE_NOT_ELIGIBLE"
  | "INVALID_QR"
  | "CONNECTION_ERROR";

// CR-07. Identifying snapshot of the attendee the scan resolved against —
// rendered as the two extra rows beneath the label. Mirrors the scan API's
// `ScanResult.attendee` shape, with `group_names` always an array.
export interface FlashAttendee {
  name: string | null;
  company: string | null;
  group_names: string[];
}

interface ScanFlashProps {
  // null hides the overlay entirely.
  result: FlashResult | null;
  // CR-07. Optional attendee snapshot. Hidden entirely when null/undefined
  // (REJECTED / INVALID_QR / CONNECTION_ERROR all flow this path).
  attendee?: FlashAttendee | null;
  // Optional single-line reason rendered under the label — the badge UUID
  // for REJECTED, the raw decoded QR text for INVALID_QR. Hidden when
  // null/empty.
  reason?: string | null;
}

interface FlashStyle {
  bg: string;
  icon: string;
  label: string;
}

const STYLES: Record<FlashResult, FlashStyle> = {
  OK: { bg: "bg-green-500", icon: "✓", label: "OK" },
  WARNING_ALREADY_IN: {
    bg: "bg-yellow-400",
    icon: "⚠",
    label: "Already in room"
  },
  WARNING_ALREADY_OUT: {
    bg: "bg-yellow-400",
    icon: "⚠",
    label: "Already exited"
  },
  WARNING_STAGE_FULL: {
    bg: "bg-yellow-400",
    icon: "⚠",
    label: "Stage at capacity"
  },
  WARNING_STAGE_NOT_ACTIVE_BEFORE: {
    bg: "bg-yellow-400",
    icon: "⚠",
    label: "Stage not active yet"
  },
  WARNING_STAGE_NOT_ACTIVE_AFTER: {
    bg: "bg-yellow-400",
    icon: "⚠",
    label: "Stage closed"
  },
  REJECTED: { bg: "bg-red-600", icon: "✕", label: "REJECTED" },
  STAGE_NOT_ELIGIBLE: {
    bg: "bg-red-600",
    icon: "✕",
    label: "Not allowed in this stage"
  },
  INVALID_QR: { bg: "bg-red-600", icon: "✕", label: "Invalid QR code" },
  CONNECTION_ERROR: { bg: "bg-red-600", icon: "✕", label: "Connection error" }
};

// Full-screen colour flash that covers the camera viewfinder. The parent
// owns timing — it sets `result` to show the flash and back to null after
// ~1.5s to hide it. The opacity transition gives a 300ms fade.
export function ScanFlash({ result, attendee, reason }: ScanFlashProps): JSX.Element {
  const visible = result !== null;
  const style = result ? STYLES[result] : null;

  // "OK" and "REJECTED" are short and rendered in the original big uppercase
  // style; everything else (the two warnings + the two new red error labels)
  // is multi-word and uses a smaller, non-uppercase variant so it fits on a
  // phone screen without wrapping.
  const isShort = result === "OK" || result === "REJECTED";
  const labelClasses = isShort
    ? "mt-4 text-5xl font-extrabold uppercase tracking-widest text-white"
    : "mt-4 px-4 text-center text-4xl font-extrabold tracking-tight text-white";

  // CR-07. The attendee snapshot rows are rendered only when an attendee
  // was actually resolved. Row 3 carries the name; row 4 joins company
  // and groups with ` · `. Either part of row 4 may be missing — only
  // the present parts are joined so there's no leading/trailing dot.
  const showAttendee = attendee != null;
  const name = attendee?.name?.trim() || "";
  const company = attendee?.company?.trim() || "";
  const groups = (attendee?.group_names ?? []).filter(
    (g) => typeof g === "string" && g.trim().length > 0
  );
  const detailParts: string[] = [];
  if (company) {
    detailParts.push(company);
  }
  detailParts.push(...groups);

  const reasonText = reason?.trim() ?? "";
  const showReason = reasonText.length > 0;

  return (
    <div
      data-testid="scan-flash"
      aria-live="assertive"
      className={`pointer-events-none absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-300 ${
        style ? style.bg : "bg-transparent"
      } ${visible ? "opacity-100" : "opacity-0"}`}
    >
      {style ? (
        <>
          <span className="text-8xl text-white" aria-hidden="true">
            {style.icon}
          </span>
          <span className={labelClasses}>{style.label}</span>
          {showAttendee && name ? (
            <span
              data-testid="scan-flash-name"
              className="mt-3 max-w-[90%] truncate px-4 text-center text-2xl font-bold text-white/95"
            >
              {name}
            </span>
          ) : null}
          {showAttendee && detailParts.length > 0 ? (
            // line-clamp-3 keeps very chatty rosters (the live MP sample
            // returned 6 groups) from pushing the snapshot off-screen.
            <span
              data-testid="scan-flash-detail"
              className="mt-1 line-clamp-3 max-w-[90%] px-4 text-center text-base font-medium leading-snug text-white/85"
            >
              {detailParts.join(" · ")}
            </span>
          ) : null}
          {showReason ? (
            // line-clamp-2 caps a very long QR payload at two lines; the
            // caller already truncates raw decoded text to ~64 chars.
            // `break-all` lets a long UUID wrap mid-string instead of
            // pushing the overlay off-screen on a narrow viewport.
            <span
              data-testid="scan-flash-reason"
              className="mt-3 line-clamp-2 max-w-[90%] break-all px-4 text-center font-mono text-base font-medium leading-snug text-white/90"
            >
              {reasonText}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
